import { HttpClient, HttpParams, HttpResponse } from "@angular/common/http";
import { inject, Injectable } from "@angular/core";
import { map, Observable, of } from "rxjs";
import { shareReplay, tap } from "rxjs/operators";
import { CacheManager, StorageType, WeightFactors } from "./cache-manager";

@Injectable({ providedIn: "root" })
export class BaseHttpService<S extends BaseHttpService<S>> {
  protected httpClient: HttpClient = inject(HttpClient);

  protected storeType: StorageType = "memory";

  public constructor () {
    CacheManager.register(this, this.storeType);
  }

  /* eslint-disable-next-line @tseslint/max-params, @unicorn/no-object-as-default-parameter */
  protected get<T>(url: string, params?: HttpParams | Record<string, string | number | boolean | ReadonlyArray<string | number | boolean>>, cache: boolean = false, maxAge: number = Infinity, weightFactors: WeightFactors = {
    accessCount: 1,
    averageResponseTime: 1,
    responseSize: 1
  }): Observable<T> {
    const cacheKey: string = this.getCacheKey(url, params);
    const cacheEntry: T | null = CacheManager.get<T>(this.constructor.name, cacheKey);

    if (cache && !CacheManager.isExpired(this.constructor.name, cacheKey, maxAge)) {
      const cachedData: T | null = cacheEntry;

      if (cachedData)
        return of(cachedData);
    }

    return this.httpClient.get<T>(url, { params: params ?? new HttpParams(), observe: "response" }).pipe(
      tap((response: HttpResponse<T>) => {
        if (cache || CacheManager.keyExists(this.constructor.name, cacheKey)) {
          const responseData: T | null = response.body;
          const responseSize: number = new Blob([ JSON.stringify(responseData) ]).size;
          /* eslint-disable-next-line @tseslint/no-non-null-assertion */
          const responseTime: number = response.headers.get("X-Response-Time") ? Number.parseFloat(response.headers.get("X-Response-Time")!) : 0;

          CacheManager.set(this.constructor.name, cacheKey, responseData, responseTime, responseSize, weightFactors);
        }
      }),
      /* eslint-disable-next-line @tseslint/no-non-null-assertion */
      map((response: HttpResponse<T>) => response.body!),
      shareReplay(1)
    );
  }

  /* eslint-disable-next-line @tseslint/class-methods-use-this */
  private getCacheKey (url: string, params?: HttpParams | Record<string, string | number | boolean | ReadonlyArray<string | number | boolean>>): string {
    if (!params)
      return url;

    const paramArray: string[] = [];

    if (params instanceof HttpParams) {
      /* TODO */
    }

    Object.entries(params).forEach((entry: [ string, unknown ]) => {
      /* eslint-disable-next-line @tseslint/restrict-template-expressions */
      paramArray.push(`${ entry[ 0 ] }:${ entry[ 1 ] }`);
    });

    return `${ url }?${ paramArray.join(",") }`;
  }
}
